// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.
//
// D-CRX-72 — the drug's administration configuration, shown at ENROLLMENT and adjustable for this
// patient: protocols (premedication + anaphylaxis kit), flushing, and the rate rule.
//
// The defaults are the drug edit form's own values, read live, so the pharmacist enrolling sees
// what will actually be administered instead of trusting that someone configured it. An adjustment
// is written to the care plan for this therapy and never back onto the drug record — one patient's
// slower first infusion must not become every patient's.

import { useEffect, useState } from 'react'
import { Beaker, Droplets, Gauge } from 'lucide-react'
import * as api from '../../api/client'
import { useProtocolLibrary } from '../../hooks/queries/carerxProtocols'
import PatientProtocolChoice, { type ChoiceKind } from './PatientProtocolChoice'
import type { ProtocolSlot } from '../../api/carerxProtocols'

const card: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 9, padding: '10px 12px', marginBottom: 8,
  background: 'var(--color-surface)',
}
const head: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--color-text)' }
const dim: React.CSSProperties = { fontSize: 11.5, color: 'var(--color-text-soft)', whiteSpace: 'pre-wrap' }
const ta: React.CSSProperties = {
  width: '100%', minHeight: 54, fontSize: 12, padding: '6px 8px', borderRadius: 7,
  border: '1px solid var(--color-border)', fontFamily: 'inherit', marginTop: 5,
}
const linkish: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, fontSize: 11.5, fontWeight: 600,
  color: 'var(--color-primary)', cursor: 'pointer',
}

export interface AdminOverrides {
  premed_override?: string
  kit_override?: string
  flush_override?: string
  rate_rule_override?: string
  /** D-CRX-89: this patient's protocol choice (absent = untouched; null id = the drug preset). */
  protocol_override?: NonNullable<api.AdminOverrideBody['protocol_override']>
}

/** The rate ladder as a sentence, because a row of numbers is not something a nurse can check. */
function rateSummary(r: api.EnrollmentAdminConfig['rate_rule']): string {
  if (!r?.model && r?.first?.start == null && r?.subsequent?.start == null) return ''
  type Leg = { start: number | null; increment: number | null; interval_min: number | null; max: number | null } | null | undefined
  const leg = (l: Leg) => {
    if (!l || (l.start == null && l.increment == null && l.max == null)) return null
    const bits = [
      l.start != null ? `start ${l.start}` : null,
      l.increment != null ? `+${l.increment}` : null,
      l.interval_min != null ? `every ${l.interval_min} min` : null,
      l.max != null ? `max ${l.max}` : null,
    ].filter(Boolean)
    return bits.join(' · ')
  }
  const first = leg(r.first), sub = leg(r.subsequent)
  return [
    r.model ? `Model: ${r.model}` : null,
    first ? `First dose — ${first}` : null,
    sub ? `Subsequent — ${sub}` : null,
    r.diluent ? `Diluent: ${r.diluent}` : null,
    r.standard_final_volume_ml != null ? `Final volume: ${r.standard_final_volume_ml} mL` : null,
    r.infusion_duration_min != null ? `Duration: ${r.infusion_duration_min} min` : null,
  ].filter(Boolean).join('\n')
}

/**
 * flush_constraints is stored as the drug record's JSON list ({text, source}), which is fine to
 * store and unreadable to show. A nurse gets the sentences; anything unparseable falls through
 * verbatim rather than being swallowed.
 */
function constraintLines(raw: string): string {
  const t = raw.trim()
  if (!t.startsWith('[') && !t.startsWith('{')) return t
  try {
    const parsed = JSON.parse(t)
    const arr = Array.isArray(parsed) ? parsed : [parsed]
    const lines = arr
      .map(x => (typeof x === 'string' ? x : x && typeof x === 'object' ? (x.text ?? x.note ?? x.value ?? '') : ''))
      .map(x => String(x).trim())
      .filter(Boolean)
      .map(x => '\u2022 ' + x)
    return lines.length ? lines.join('\n') : t
  } catch { return t }
}

export default function EnrollmentAdminConfig({ formularyId, therapyId, value, onChange }: {
  formularyId: string
  /** Editing an existing enrollment: load what was already adjusted for this patient. */
  therapyId?: string
  value: AdminOverrides
  onChange: (next: AdminOverrides) => void
}) {
  const [cfg, setCfg] = useState<api.EnrollmentAdminConfig | null>(null)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const library = useProtocolLibrary()

  // Hydrate once per therapy, not on every render of the parent: re-seeding from the server after
  // the pharmacist has started typing would silently undo their edit.
  const [hydrated, setHydrated] = useState<string | null>(null)
  useEffect(() => {
    if (!formularyId) { setCfg(null); return }
    let live = true
    api.getEnrollmentAdminConfig(formularyId, therapyId)
      .then(d => {
        if (!live) return
        setCfg(d)
        const key = `${formularyId}:${therapyId ?? ''}`
        if ((d.overrides || d.protocol_overrides) && hydrated !== key) {
          setHydrated(key)
          const o = d.overrides ?? {}
          // The rate rule is stored as JSON ({note}) but edited as a sentence — unwrap it back.
          let rate = o.rate_rule_override ?? ''
          if (rate.trim().startsWith('{')) { try { rate = JSON.parse(rate).note ?? rate } catch { /* keep raw */ } }
          const po = d.protocol_overrides
          onChange({
            premed_override: o.premed_override ?? '', kit_override: o.kit_override ?? '',
            flush_override: o.flush_override ?? '', rate_rule_override: rate,
            // D-CRX-89: the saved choice comes back unchanged (no reason needed to keep it).
            ...(po ? { protocol_override: { premed_protocol_id: po.premed_protocol_id, kit_protocol_id: po.kit_protocol_id,
              flush_protocol_id: po.flush_protocol_id, reason: '' } } : {}),
          })
        }
      })
      .catch(() => { if (live) setCfg(null) })   // fail-quiet: never block an enrollment on this read
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formularyId, therapyId])

  if (!formularyId || !cfg) return null

  // D-CRX-89 — one slot's choice for this patient. Reasons from several slots are kept together for the audit.
  const protocols = library.data?.protocols ?? []
  const choice = value.protocol_override
  const chosen = (slot: ProtocolSlot) => (choice ? choice[`${slot}_protocol_id`] : null)
  const choose = (slot: ProtocolSlot, id: string | null, reason: string, kind: ChoiceKind) => {
    const base = choice ?? { premed_protocol_id: null, kit_protocol_id: null, flush_protocol_id: null, reason: '' }
    const why = `${slot}: ${reason}`
    // One save carries one kind: it is an "update to latest" only when every change in it is one.
    const nextKind: ChoiceKind = kind === 'choice' || (base.reason && base.kind !== 'update_latest') ? 'choice' : 'update_latest'
    onChange({ ...value, protocol_override: { ...base, [`${slot}_protocol_id`]: id, reason: base.reason ? `${base.reason}; ${why}` : why, kind: nextKind } })
  }
  const picker = (slot: ProtocolSlot) => (
    <PatientProtocolChoice key={slot} slot={slot} presetId={cfg.protocol_ids?.[slot] ?? null} chosenId={chosen(slot)}
      saved={cfg.protocol_slots?.find(s => s.slot === slot) ?? null}
      savedText={cfg.protocol_overrides?.[slot] ?? null}
      drugText={(slot === 'flush' ? cfg.flush?.protocol : cfg.protocols?.[slot]) ?? null}
      protocols={protocols} onChoose={(id, reason, kind) => choose(slot, id, reason, kind)} />
  )

  const blocks: { key: Exclude<keyof AdminOverrides, 'protocol_override'>; icon: typeof Beaker; title: string; body: string; empty: string }[] = [
    {
      key: 'premed_override', icon: Beaker, title: 'Protocols',
      // D-CRX-89: the premed / kit lines render per slot below (this patient's own, never the drug's over a "none").
      body: cfg.protocols?.observation_minutes != null ? `Observation: ${cfg.protocols.observation_minutes} min` : '',
      empty: '',
    },
    {
      key: 'flush_override', icon: Droplets, title: 'Flush',
      // Constraints are drug properties, not a protocol; the flush protocol renders per slot below.
      body: cfg.flush?.constraints ? `Compatibility constraints:\n${constraintLines(cfg.flush.constraints)}` : '',
      empty: '',
    },
    { key: 'rate_rule_override', icon: Gauge, title: 'Rate rule', body: rateSummary(cfg.rate_rule), empty: 'No rate rule configured for this drug.' },
  ]

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11.5, color: 'var(--color-text-soft)', marginBottom: 6 }}>
        From the drug record — adjust any of it for <b>this patient</b>. An adjustment is saved on this
        therapy's care plan; the drug's own configuration is never changed.
      </div>
      {blocks.map(b => {
        const Icon = b.icon
        const isOpen = !!open[b.key]
        const override = value[b.key] ?? ''
        return (
          <div key={b.key} style={card}>
            <div style={head}>
              <Icon size={13} color="var(--color-primary)" />
              {b.title}
              {override.trim() && (
                <span style={{ fontSize: 10, fontWeight: 700, background: '#fff7e6', color: '#93610f', borderRadius: 100, padding: '1px 8px' }}>
                  adjusted for this patient
                </span>
              )}
              <button type="button" style={{ ...linkish, marginLeft: 'auto' }}
                onClick={() => setOpen(o => ({ ...o, [b.key]: !isOpen }))}>
                {isOpen ? 'Done' : override.trim() ? 'Edit adjustment' : 'Adjust for this patient'}
              </button>
            </div>
            {(b.body || b.empty) && <div style={{ ...dim, marginTop: 4 }}>{b.body || b.empty}</div>}
            {b.key === 'premed_override' && <>{picker('premed')}{picker('kit')}</>}
            {b.key === 'flush_override' && picker('flush')}
            {isOpen && (
              <>
                <textarea style={ta} value={override} placeholder={`What is different for this patient? (${b.title.toLowerCase()})`}
                  onChange={e => onChange({ ...value, [b.key]: e.target.value })} />
                {override.trim() && (
                  <button type="button" style={{ ...linkish, color: '#b23a48', marginTop: 4 }}
                    onClick={() => onChange({ ...value, [b.key]: '' })}>Clear the adjustment</button>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
